import * as THREE from "three";

const worleyNoise = `
    vec2 hash2(vec2 p) {
        return fract(sin(vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3))))*43758.5453);
    }
    float worley(vec2 p) {
        vec2 n = floor(p);
        vec2 f = fract(p);
        float d = 1.0;
        for(int j=-1; j<=1; j++) {
            for(int i=-1; i<=1; i++) {
                vec2 g = vec2(float(i),float(j));
                vec2 o = hash2(n + g);
                o = 0.5 + 0.5*sin(uTime * uTimeSpeed + 6.2831*o);
                vec2 r = g + o - f;
                d = min(d, dot(r,r));
            }
        }
        return 1.0 - sqrt(d);
    }
`;

const vertexShader = `
    varying vec2 vUv;
    varying vec3 vNormalWorld;
    varying vec3 vPositionWorld;
    void main() {
        vUv = uv;
        vNormalWorld = normalize(mat3(modelMatrix) * normal);
        vPositionWorld = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const fragmentShader = `
    uniform float uTime;
    uniform vec3 uColor;
    uniform float uOpacity;
    uniform float uTimeSpeed;
    uniform float uDriftSpeed;
    uniform float uNoiseScale;
    uniform float uNoiseStretch;
    uniform float uSmoothTop;
    uniform float uSmoothBottom;
    uniform float uFresnelPower;
    uniform vec3 uCameraPosition;

    varying vec2 vUv;
    varying vec3 vNormalWorld;
    varying vec3 vPositionWorld;

    ${worleyNoise}

    void main() {
        vec2 drift = vec2(uTime * uDriftSpeed, uTime * uDriftSpeed * 0.2);
        vec2 stretchedUv = vec2(vUv.x * uNoiseScale, vUv.y * uNoiseScale * uNoiseStretch) + drift;
        
        float noise = worley(stretchedUv);
        float verticalSmooth = smoothstep(0.0, uSmoothBottom, vUv.y) * smoothstep(1.0, uSmoothTop, vUv.y);

        vec3 viewDirection = normalize(uCameraPosition - vPositionWorld);
        float fresnel = pow(abs(dot(vNormalWorld, viewDirection)), uFresnelPower);

        float alpha = noise * fresnel * verticalSmooth * uOpacity;
        gl_FragColor = vec4(uColor, alpha);
    }
`;

export default class GodRays {
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.worldScene;
    this.time = this.experience.time;
    this.camera = this.experience.worldCamera;
    this.debug = this.experience.debug;

    this.config = {
      // Geometry (Shape)
      radiusTop: 0.1,
      radiusBottom: 4.2763,
      height: 18.564,

      // Transform
      posX: 2.52,
      posY: 4.9,
      posZ: -6.7,
      rotX: -0.88592,
      rotY: 0,
      rotZ: 0,

      // Shader & Animation
      color: "#364f78",
      opacity: 1.0,
      noiseScale: 37.2,    // Density
      noiseStretch: 0.0,   // Stretch (Bandes)
      timeSpeed: 1.0,      // Flicker Speed
      driftSpeed: 0.5,     // Drift Speed
      fresnelPower: 3.051, // Fresnel Power
      
      // Fallbacks smooth pour éviter les coupures
      smoothBottom: 0.1,
      smoothTop: 0.9
    };

    this.setInstance();
    if (this.debug.active) this.setDebug();
  }

  setInstance() {
    this.geometry = new THREE.CylinderGeometry(
      this.config.radiusTop,
      this.config.radiusBottom,
      this.config.height,
      64,
      1,
      true
    );

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      vertexShader,
      fragmentShader,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(this.config.color) },
        uOpacity: { value: this.config.opacity },
        uTimeSpeed: { value: this.config.timeSpeed },
        uDriftSpeed: { value: this.config.driftSpeed },
        uNoiseScale: { value: this.config.noiseScale },
        uNoiseStretch: { value: this.config.noiseStretch },
        uSmoothTop: { value: this.config.smoothTop },
        uSmoothBottom: { value: this.config.smoothBottom },
        uFresnelPower: { value: this.config.fresnelPower },
        uCameraPosition: { value: new THREE.Vector3() },
      },
    });

    this.ray = new THREE.Mesh(this.geometry, this.material);
    this.updateTransform();
    this.scene.add(this.ray);
  }

  updateGeometry() {
    if (this.geometry) this.geometry.dispose();
    this.geometry = new THREE.CylinderGeometry(
      this.config.radiusTop,
      this.config.radiusBottom,
      this.config.height,
      64,
      1,
      true
    );
    this.ray.geometry = this.geometry;
  }

  updateTransform() {
    this.ray.position.set(this.config.posX, this.config.posY, this.config.posZ);
    this.ray.rotation.set(this.config.rotX, this.config.rotY, this.config.rotZ);
  }

  setDebug() {
    if (!this.debug.ui) return;
    const folder = this.debug.ui.addFolder("Ultimate Godray Builder");

    const geoFolder = folder.addFolder("Geometry (Shape)");
    geoFolder.add(this.config, "radiusTop", 0.0, 10).name("Top Radius").onChange(() => this.updateGeometry());
    geoFolder.add(this.config, "radiusBottom", 0.0, 10).name("Bottom Radius").onChange(() => this.updateGeometry());
    geoFolder.add(this.config, "height", 0.1, 40).name("Height").onChange(() => this.updateGeometry());

    const posFolder = folder.addFolder("Transform");
    posFolder.add(this.config, "posX", -30, 30).name("X").onChange(() => this.updateTransform());
    posFolder.add(this.config, "posY", -30, 30).name("Y").onChange(() => this.updateTransform());
    posFolder.add(this.config, "posZ", -30, 30).name("Z").onChange(() => this.updateTransform());
    posFolder.add(this.config, "rotX", -Math.PI, Math.PI).name("Rotation X").onChange(() => this.updateTransform());

    const shaderFolder = folder.addFolder("Shader & Animation");
    shaderFolder.addColor(this.config, "color").onChange(v => this.material.uniforms.uColor.value.set(v));
    shaderFolder.add(this.config, "noiseScale", 0, 100).name("Density").onChange(v => this.material.uniforms.uNoiseScale.value = v);
    shaderFolder.add(this.config, "noiseStretch", 0, 1).name("Stretch (Bandes)").onChange(v => this.material.uniforms.uNoiseStretch.value = v);
    shaderFolder.add(this.config, "timeSpeed", 0, 2).name("Flicker Speed").onChange(v => this.material.uniforms.uTimeSpeed.value = v);
    shaderFolder.add(this.config, "driftSpeed", 0, 2).name("Drift Speed").onChange(v => this.material.uniforms.uDriftSpeed.value = v);
    shaderFolder.add(this.config, "fresnelPower", 1, 10).name("Fresnel Power").onChange(v => this.material.uniforms.uFresnelPower.value = v);
  }

  update() {
    this.material.uniforms.uTime.value = this.time.elapsed * 0.001; //
    if (this.camera?.instance) {
        this.material.uniforms.uCameraPosition.value.copy(this.camera.instance.position); //
    }
  }
}